
import { Component, Vue, Watch, Prop, Provide } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"

import FileService from "@/services/file-service"
import { MapProvider } from "@/services/mapProvider"

import { CaseError } from "@/types/caseError"

import LayoutHost from "@/components/CaseDetails/Layout/LayoutHost.vue"
import { CaseDetails, TemplateDetails, User } from "@/types"

import {
	applyTemplate
} from "@/scripts/caseDetails"
const caseStore = namespace("cases")

@Component({
	components: {
		"layout-host": LayoutHost,
		"desktop": () => import("@/components/CaseDetails/Layout/Desktop.vue")
	}
})
export default class QuickCreate extends Vue {
	@Provide("mapProvider") mapProvider: MapProvider = new MapProvider()
	@Provide("fileService") fileService: FileService = new FileService(this.caseFilesChanged)

	@Prop({ default: null })
	value!: { templateId: string } | null

	@caseStore.Getter
	private getBlankCase!: () => CaseDetails

	@caseStore.Action public detailsLoad!: () => Promise<void>;

	@Getter("getMe") public me!: User
	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	@caseStore.Action public caseCreate!: (caseDetails: CaseDetails) => {
		created: CaseDetails,
		errors: CaseError[]
	};

	private currentCase: CaseDetails | null = null
	private currentTemplate: TemplateDetails | null = null

	private valid: boolean = false
	private creating: boolean = false

	private canCreateCase: boolean = false;
	private canCreateRecord: boolean = false;

	private createdCaseNumber: string | null = null

	get open (): boolean {
		return !!this.value
	}

	set open (value: boolean) {
		if (!value) {
			this.$emit("input", null)
		}
	}

	get caseTitle () {
		if (!this.currentCase) return ""
		return "Create"
	}

	private get alwaysPromoteToCase (): boolean {
		return !!this.currentTemplate && !!this.currentTemplate.alwaysPromoted
	}

	@Watch("value")
	valueUpdated (newValue: { templateId: string } | null) {
		if (newValue) {
			this.detailsLoad().then(() => {
				const newCase = this.getBlankCase()
				if (newValue.templateId) {
					newCase.templateId = newValue.templateId
				}
				this.currentCase = newCase
			})
		} else {
			this.currentCase = null
		}
	}

	private async onTemplateChange (template: TemplateDetails | null) {
		this.currentTemplate = template
		if (this.currentCase && template) {
			applyTemplate(this.currentCase!, template!)
		}
	}

	private async permissionsChanged ({ canCreateCase, canCreateRecord }: { canCreateCase: boolean, canCreateRecord: boolean }) {
		this.canCreateCase = canCreateCase
		this.canCreateRecord = canCreateRecord
	}

	private async createNew (isCase: boolean) {
		if (this.creating || !this.currentCase) return

		this.creating = true
		try {
			this.currentCase.isCase = isCase
			const { created, errors }: { created: CaseDetails, errors: CaseError[] } = await this.caseCreate(this.currentCase)

			if (errors) {
				errors.forEach(e => {
					this.$bvToast.toast(`There was a problem adding the field ${e.field.title}: ${e.message}`, {
						title: `Error adding field ${e.field.title}`,
						variant: "danger",
						solid: true,
						autoHideDelay: 10000000
					})
				})
			}

			if (this.fileService.uploadFiles.length === 0) {
				this.$bvToast.toast(`Created #${created.caseNumber}`, {
					title: "Created",
					href: `/#/cases/${encodeURIComponent(created.caseNumber as string)}`,
					variant: "success",
					solid: true,
					autoHideDelay: 5000
				})

				this.creating = false
				this.close()
			} else {
				this.createdCaseNumber = created.caseNumber
				this.fileService.uploadPendingFiles(created.id!)
			}
		} catch (error) {
			this.creating = false
		}
	}

	private close () {
		this.$emit("input", null)
	}

	private caseFilesChanged () {
		if (this.creating) {
			this.creating = false

			this.$bvToast.toast(`Created #${this.createdCaseNumber}`, {
				title: "Created",
				href: `/#/cases/${encodeURIComponent(this.createdCaseNumber as string)}`,
				variant: "success",
				solid: true,
				autoHideDelay: 5000
			})

			this.createdCaseNumber = null
			this.close()
		}
	}
}

